import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import {
  OrderIncompleteDeliveryReason,
  OrderUnresolvedIncompleteDelivery,
} from '@/models/OrderIncompleteDelivery';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface IncompleteDeliveryReportPanelProps {
  report: OrderUnresolvedIncompleteDelivery;
  onResolve?: (incompleteDeliveryId: string) => Promise<void>;
}

const reasonLabels: Record<OrderIncompleteDeliveryReason, string> = {
  [OrderIncompleteDeliveryReason.ItemsMissing]: 'Items missing',
  [OrderIncompleteDeliveryReason.ItemDamaged]: 'Item damaged',
  [OrderIncompleteDeliveryReason.Other]: 'Other issue',
};

const IncompleteDeliveryReportPanel = ({
  report,
  onResolve,
}: IncompleteDeliveryReportPanelProps) => {
  return (
    <div
      className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4"
      data-testid="order-details-incomplete-delivery-report"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2 text-base font-semibold text-amber-900">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            Incomplete delivery reported
          </div>
          <div className="space-y-2 text-sm text-amber-950">
            <div>
              <span className="font-semibold">Reason:</span> {reasonLabels[report.reason]}
            </div>
            {report.comment && (
              <div>
                <span className="font-semibold">Comment:</span>{' '}
                <span data-testid="order-details-incomplete-delivery-comment">
                  {report.comment}
                </span>
              </div>
            )}
          </div>
        </div>

        {onResolve && (
          <Confirm
            onConfirm={() => onResolve(report.id)}
            title="Resolve incomplete delivery"
            message="Are you sure this incomplete delivery report has been resolved?"
          >
            {(handleClick, loading) => (
              <Button
                size="xs"
                variant="secondary"
                className="shrink-0 border-amber-400 bg-white text-amber-950 hover:bg-amber-100"
                onClick={handleClick}
                loading={loading}
                data-testid="order-details-resolve-incomplete-delivery-button"
              >
                Resolve
              </Button>
            )}
          </Confirm>
        )}
      </div>
    </div>
  );
};

export default IncompleteDeliveryReportPanel;
